/**
 * Prioris · Quality Control — Inspection & Test Plan (ITP) + Pouring Records.
 *
 *  Two views in one component:
 *  1) Master List — every pour for the active project (date, element, loads,
 *     m³, V/NV tally) + "New pour" button.
 *  2) Pour Detail — header fields, V/NV limits editor, a consolidated
 *     "Delivery & Placement" block (552-D-1…D-5 test chips + truckload grid
 *     with INSTANT client-side V/NV flagging as the user types) and the
 *     full ITP reference underneath.
 *
 *  Sidebar integration (Prioris shell):
 *    PriorisApp passes `selectedPourId` when the user picks a mini-ITP from
 *    the sidebar and `newPourSignal` (a monotonically-increasing number) when
 *    "+ New pour" is clicked.  A `onPoursChanged` callback is fired after
 *    any mutation so the sidebar can refresh.
 *
 *  Note: this module does NOT touch the existing Concrete strength
 *  conformity / Bayesian projection views.  Sample IDs captured here are
 *  designed to be referenced there as read-only linkbacks later.
 */
import { useEffect, useMemo, useState } from 'react';
import { fetchSln } from '../nexus/nexusHelpers';
import {
  Plus, ChevronRight, Beaker, Truck, Camera, FileText, Trash2, X, AlertCircle,
  CheckCircle2, Info, Layers, ExternalLink, Search,
} from 'lucide-react';

const API = '/api/prioris/qc/itp';
const PASS = '#22c55e', FAIL = '#ef4444', MUTED = '#94a3b8';

/* ── client-side image compression (canvas) ───────────────────────────── */
async function compressImage(file, maxDim = 1600, quality = 0.75) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = reject;
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onerror = reject;
    i.onload = () => resolve(i);
    i.src = dataUrl;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const out = canvas.toDataURL('image/jpeg', quality);
  return { name: file.name, data: out, size: Math.round((out.length * 3) / 4) };
}

/* ── Live verdict helpers (mirror backend _evaluate_record) ─────────── */
function verdictFor(field, value, pour) {
  if (value === '' || value === null || value === undefined || Number.isNaN(value)) return 'NA';
  const n = Number(value);
  if (Number.isNaN(n)) return 'NA';
  if (field === 'slump_mm') {
    const lo = pour?.slump_min_mm, hi = pour?.slump_max_mm;
    if (lo == null || hi == null) return 'NA';
    return (n >= lo && n <= hi) ? 'V' : 'NV';
  }
  if (field === 'temp_c') {
    const lo = pour?.temp_min_c, hi = pour?.temp_max_c;
    let ok = true;
    if (lo != null) ok = ok && n >= lo;
    if (hi != null) ok = ok && n <= hi;
    if (lo == null && hi == null) return 'NA';
    return ok ? 'V' : 'NV';
  }
  if (field === 'air_pct') {
    const lo = pour?.air_min_pct, hi = pour?.air_max_pct;
    if (lo == null || hi == null) return 'NA';
    return (n >= lo && n <= hi) ? 'V' : 'NV';
  }
  return 'NA';
}
function overallVerdict(v) {
  const arr = [v.slump, v.temp, v.air].filter(x => x === 'V' || x === 'NV');
  if (!arr.length) return 'NA';
  return arr.some(x => x === 'NV') ? 'NV' : 'V';
}
function verdictColorBg(v) {
  if (v === 'V') return 'rgba(34,197,94,0.12)';
  if (v === 'NV') return 'rgba(239,68,68,0.14)';
  return null;
}
function verdictBorderColor(v) {
  if (v === 'V') return PASS;
  if (v === 'NV') return FAIL;
  return null;
}

/* =======================================================================
   Entry component — Master List (default view) with "New pour" modal and
   drill-down into the detail view.
   ======================================================================= */
export default function PriorisQCItp({ t, project, selectedPourId, newPourSignal, onPoursChanged }) {
  const [pours, setPours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openPour, setOpenPour] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [itpCatalog, setItpCatalog] = useState(null);

  const load = async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const [pr, cat] = await Promise.all([
        fetchSln(`${API}/pours?project_id=${project.id}`),
        fetchSln(`${API}/catalog?project_id=${project.id}`),
      ]);
      setPours(pr.pours || []);
      setItpCatalog(cat.itps || []);
      if (onPoursChanged) onPoursChanged(pr.pours || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project?.id]);

  // React to sidebar "mini-ITP" selection
  useEffect(() => {
    if (selectedPourId == null) return;
    const p = pours.find(x => x.id === selectedPourId);
    if (p) setOpenPour(p);
  }, [selectedPourId, pours]);

  // React to sidebar "+ New pour" button
  useEffect(() => {
    if (newPourSignal && newPourSignal > 0) {
      setOpenPour(null);
      setShowNew(true);
    }
    // eslint-disable-next-line
  }, [newPourSignal]);

  if (openPour) {
    return <PourDetail t={t} pour={openPour}
      onBack={() => { setOpenPour(null); load(); }}
      onReload={() => { load(); }} />;
  }

  return (
    <div className="p-4 max-w-6xl mx-auto text-[12px]" data-testid="prioris-itp">
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: t.accent }}>
            Quality Control · Inspection & Test Plan
          </div>
          <h2 className="text-lg font-bold" style={{ color: t.text }}>Pouring Records · Master List</h2>
          <p className="text-[11px]" style={{ color: t.textMuted }}>
            Tracks every concrete pour event for {project?.asset_name || 'this asset'}. Each pour groups its truckloads with slump / temp / air tests and photo evidence. Click a row to open its Pouring Record sheet.
          </p>
        </div>
        <button onClick={() => setShowNew(true)}
          className="px-3 py-1.5 rounded font-semibold flex items-center gap-1.5"
          style={{ background: t.accent, color: t.bg, fontSize: 12 }}
          data-testid="itp-new-pour-btn">
          <Plus className="w-3.5 h-3.5" /> New Pouring Record
        </button>
      </div>

      {/* Applicable ITP banner */}
      {Array.isArray(itpCatalog) && itpCatalog.length > 0 && (
        <div className="mb-3 p-2 rounded flex items-start gap-2"
          style={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.text }}>
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: t.accent }} />
          <div className="text-[11px]" style={{ color: t.textMuted }}>
            {itpCatalog.filter(x => x.applicable).length === 0
              ? 'No spec tables applied to this project yet — go to Spec Library to apply FP-24 §552 (or your contract spec) to unlock the matching ITP.'
              : <>Applicable ITP: <strong style={{ color: t.text }}>{itpCatalog.filter(x => x.applicable).map(x => x.label_short).join(' · ')}</strong>. Per-row acceptance criteria visible in each pour's sheet.</>
            }
          </div>
        </div>
      )}

      {/* Master list */}
      <div className="rounded overflow-hidden" style={{ border: `1px solid ${t.cardBorder}` }}>
        <table className="w-full text-[11px]" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr style={{ background: t.inputBg }}>
              {['Date', 'Ref / Element', 'BIM / Section ID', 'Clause', 'Planned m³', 'Loads', 'Total m³', 'Samples', 'V / NV', 'Status', ''].map((h, i) => (
                <th key={i} className="text-left px-2 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.textMuted }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="p-4 text-center" style={{ color: t.textDim }}>Loading…</td></tr>}
            {!loading && pours.length === 0 && (
              <tr><td colSpan={11} className="p-5 text-center" style={{ color: t.textDim }}>
                No pours yet — click <strong style={{ color: t.text }}>New Pouring Record</strong> to start.
              </td></tr>
            )}
            {pours.map(p => {
              const s = p.summary || {};
              const vn = (s.v_count + s.nv_count) > 0
                ? `${s.v_count} V / ${s.nv_count} NV`
                : '—';
              return (
                <tr key={p.id} onClick={() => setOpenPour(p)}
                  className="cursor-pointer hover:brightness-110"
                  style={{ background: t.card, color: t.text }}
                  data-testid={`itp-pour-${p.id}`}>
                  <td className="px-2 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
                    <div>{p.pour_date || '—'}</div>
                    <div className="text-[10px]" style={{ color: t.textDim }}>{p.start_time}</div>
                  </td>
                  <td className="px-2 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
                    <div className="font-semibold">{p.pour_ref || p.id.slice(0, 6).toUpperCase()}</div>
                    <div className="text-[10px]" style={{ color: t.textMuted }}>{p.element || <em>no element</em>}</div>
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[10px]" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.textMuted }}>
                    {p.bim_id || '—'}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[10px]" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.accent }}>
                    {p.spec_rubrica_code || '—'}
                  </td>
                  <td className="px-2 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>{p.planned_volume_m3 ?? '—'}</td>
                  <td className="px-2 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>{s.loads ?? 0}</td>
                  <td className="px-2 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>{s.total_volume_m3 ?? 0}</td>
                  <td className="px-2 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>{s.samples ?? 0}</td>
                  <td className="px-2 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
                    <span style={{ color: s.nv_count > 0 ? FAIL : (s.v_count > 0 ? PASS : MUTED) }}>{vn}</span>
                  </td>
                  <td className="px-2 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase"
                      style={{ background: p.status === 'closed' ? 'rgba(34,197,94,0.15)' : 'rgba(59,130,246,0.15)', color: p.status === 'closed' ? PASS : '#60a5fa' }}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-right" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
                    <ChevronRight className="w-3.5 h-3.5 inline" style={{ color: t.textDim }} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showNew && <NewPourModal t={t} project={project} onClose={() => setShowNew(false)}
        onCreated={(p) => { setShowNew(false); setOpenPour(p); load(); }} />}
    </div>
  );
}

/* ====================================================================== */
function NewPourModal({ t, project, onClose, onCreated }) {
  const [form, setForm] = useState({
    pour_ref: '', element: '', bim_id: '', spec_rubrica_code: '', spec_table_key: '',
    planned_volume_m3: '', pour_date: new Date().toISOString().slice(0, 10),
    start_time: new Date().toISOString().slice(11, 16), site_responsible: '',
    mix_design_ref: '', producer: '', ambient_temp_c: '',
  });
  const [sections, setSections] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!project?.id) return;
    fetchSln(`/api/prioris/spec-catalog/project/${project.id}/selections`)
      .then(r => setSections(r.selections || []))
      .catch(() => setSections([]));
  }, [project?.id]);

  const clauseOpts = useMemo(() => {
    const out = [];
    for (const s of sections) {
      if (s.family !== 'concrete_mix') continue;
      for (const r of (s.rubrica_details || [])) {
        out.push({ table_key: s.table_key, code: r.code, label: r.label });
      }
    }
    return out;
  }, [sections]);

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        project_id: project.id,
        itp_key: 'FP24-552',
        pour_ref: form.pour_ref || null,
        element: form.element,
        bim_id: form.bim_id,
        spec_table_key: form.spec_table_key || null,
        spec_rubrica_code: form.spec_rubrica_code || null,
        planned_volume_m3: form.planned_volume_m3 === '' ? null : Number(form.planned_volume_m3),
        pour_date: form.pour_date,
        start_time: form.start_time,
        site_responsible: form.site_responsible,
        mix_design_ref: form.mix_design_ref,
        producer: form.producer,
        ambient_temp_c: form.ambient_temp_c === '' ? null : Number(form.ambient_temp_c),
      };
      const created = await fetchSln(`${API}/pours`, { method: 'POST', body: JSON.stringify(body) });
      onCreated(created);
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="rounded-lg w-full max-w-xl" style={{ background: t.sidebar, border: `1px solid ${t.cardBorder}`, color: t.text }}>
        <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: t.cardBorder }}>
          <Truck className="w-4 h-4" style={{ color: t.accent }} />
          <div className="flex-1">
            <div className="text-sm font-bold">New Pouring Record</div>
            <div className="text-[11px]" style={{ color: t.textMuted }}>Create a pour header. You can add truckloads right after.</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5" data-testid="new-pour-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3 text-[11px]">
          <div className="grid grid-cols-2 gap-3">
            <TxtField t={t} label="Pour reference (e.g. PR-0001)"
              value={form.pour_ref} onChange={v => setForm({ ...form, pour_ref: v })} testid="new-pour-ref" />
            <TxtField t={t} label="Date" type="date"
              value={form.pour_date} onChange={v => setForm({ ...form, pour_date: v })} />
            <TxtField t={t} label="Start time" type="time"
              value={form.start_time} onChange={v => setForm({ ...form, start_time: v })} />
            <TxtField t={t} label="Element being cast"
              value={form.element} onChange={v => setForm({ ...form, element: v })}
              placeholder="e.g. Pier 12 footing, Deck span 3…"
              testid="new-pour-element" />
            <TxtField t={t} label="BIM / Section ID (Dalux · Aconex · Procore)"
              value={form.bim_id} onChange={v => setForm({ ...form, bim_id: v })}
              placeholder="e.g. B1-C12-P03 or Aconex ref"
              testid="new-pour-bim" />
            <div>
              <div className="text-[10px] font-semibold mb-0.5" style={{ color: t.textDim }}>Clause (from Spec Library)</div>
              <select value={form.spec_rubrica_code}
                onChange={e => {
                  const opt = clauseOpts.find(o => o.code === e.target.value);
                  setForm({ ...form, spec_rubrica_code: opt?.code || '', spec_table_key: opt?.table_key || '' });
                }}
                className="w-full rounded px-2 py-1"
                style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text, fontSize: 11 }}
                data-testid="new-pour-clause">
                <option value="">—</option>
                {clauseOpts.map(o => <option key={o.code} value={o.code}>{o.code} · {o.label}</option>)}
              </select>
            </div>
            <TxtField t={t} label="Planned volume (m³)" type="number"
              value={form.planned_volume_m3} onChange={v => setForm({ ...form, planned_volume_m3: v })} />
            <TxtField t={t} label="Mix design ref"
              value={form.mix_design_ref} onChange={v => setForm({ ...form, mix_design_ref: v })} />
            <TxtField t={t} label="Producer / batching plant"
              value={form.producer} onChange={v => setForm({ ...form, producer: v })} />
            <TxtField t={t} label="Site responsible engineer"
              value={form.site_responsible} onChange={v => setForm({ ...form, site_responsible: v })} />
            <TxtField t={t} label="Ambient temp (°C)" type="number"
              value={form.ambient_temp_c} onChange={v => setForm({ ...form, ambient_temp_c: v })} />
          </div>
        </div>
        <div className="px-4 py-3 border-t flex justify-end gap-2" style={{ borderColor: t.cardBorder }}>
          <button onClick={onClose} className="px-3 py-1.5 rounded" style={{ background: t.inputBg, color: t.textMuted }}>Cancel</button>
          <button onClick={submit} disabled={saving} className="px-3 py-1.5 rounded font-semibold"
            style={{ background: t.accent, color: t.bg, opacity: saving ? 0.6 : 1 }}
            data-testid="new-pour-submit">
            {saving ? 'Creating…' : 'Create pour'}
          </button>
        </div>
      </div>
    </div>
  );
}

const TxtField = ({ t, label, value, onChange, type = 'text', placeholder, testid }) => (
  <div>
    <div className="text-[10px] font-semibold mb-0.5" style={{ color: t.textDim }}>{label}</div>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full rounded px-2 py-1"
      style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text, fontSize: 11 }}
      data-testid={testid} />
  </div>
);

/* ====================================================================== */
function PourDetail({ t, pour, onBack, onReload }) {
  const [pourState, setPourState] = useState(pour);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({});
  const [saving, setSaving] = useState(false);
  const [itp, setItp] = useState(null);

  const load = async () => {
    try {
      const r = await fetchSln(`${API}/pours/${pour.id}/records`);
      setPourState(r.pour);
      setRecords(r.records || []);
      setSummary(r.summary || {});
    } catch (e) { /* ignore */ }
    try {
      const c = await fetchSln(`${API}/catalog/${pour.itp_key || 'FP24-552'}`);
      setItp(c);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pour.id]);

  const patchPour = async (patch) => {
    setPourState(prev => ({ ...prev, ...patch }));
    try {
      await fetchSln(`${API}/pours/${pour.id}`, { method: 'PATCH', body: JSON.stringify(patch) });
      if (onReload) onReload();
    } catch (e) { alert(e.message); }
  };

  const addRow = async () => {
    setSaving(true);
    try {
      const created = await fetchSln(`${API}/pours/${pour.id}/records`, {
        method: 'POST', body: JSON.stringify({ pour_id: pour.id }),
      });
      setRecords(prev => [...prev, { ...created, cumulative_m3: summary.total_volume_m3, verdict: { overall: 'NA' } }]);
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  const patchRow = async (rec_id, patch) => {
    try {
      await fetchSln(`${API}/pours/${pour.id}/records/${rec_id}`, {
        method: 'PATCH', body: JSON.stringify(patch),
      });
      load();
    } catch (e) { alert(e.message); }
  };

  const deleteRow = async (rec_id) => {
    if (!window.confirm('Delete this truckload row?')) return;
    try {
      await fetchSln(`${API}/pours/${pour.id}/records/${rec_id}`, { method: 'DELETE' });
      load();
    } catch (e) { alert(e.message); }
  };

  // Extract the Delivery & Placement phase rows for the consolidated block
  const deliveryPhase = useMemo(
    () => itp?.phases?.find(p => p.key === 'delivery') || null, [itp]
  );
  const otherPhases = useMemo(
    () => (itp?.phases || []).filter(p => p.key !== 'delivery'), [itp]
  );

  return (
    <div className="p-4 max-w-7xl mx-auto text-[12px]" data-testid="itp-pour-detail">
      {/* Header */}
      <button onClick={onBack} className="text-[11px] mb-2 flex items-center gap-1"
        style={{ color: t.accent }} data-testid="itp-back">
        <ChevronRight className="w-3 h-3 rotate-180" /> Back to Pouring Records
      </button>
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: t.accent }}>
            Pouring Record · {pourState.pour_ref || pourState.id.slice(0, 6).toUpperCase()}
            {pourState.bim_id && <span className="ml-2 px-1.5 py-0.5 rounded font-mono"
              style={{ background: t.inputBg, color: t.textMuted, fontSize: 9 }}
              title="BIM / Section ID">
              <Layers className="w-2.5 h-2.5 inline mr-0.5" />{pourState.bim_id}
            </span>}
          </div>
          <h2 className="text-lg font-bold" style={{ color: t.text }}>{pourState.element || 'Unnamed element'}</h2>
          <p className="text-[11px]" style={{ color: t.textMuted }}>
            {pourState.pour_date} {pourState.start_time} · {pourState.producer || 'producer TBC'} · {pourState.site_responsible || 'site engineer TBC'}
            {pourState.spec_rubrica_code && <span className="ml-1 font-mono" style={{ color: t.accent }}>· {pourState.spec_rubrica_code}</span>}
          </p>
        </div>
        <div className="flex gap-1.5">
          <SummaryChip label="Loads" value={summary.loads ?? 0} color={t.accent} t={t} />
          <SummaryChip label="Total m³" value={summary.total_volume_m3 ?? 0} color={t.accent} t={t} />
          <SummaryChip label="Samples" value={summary.samples ?? 0} color={PASS} t={t} testid="itp-summary-samples" />
          <SummaryChip label="V" value={summary.v_count ?? 0} color={PASS} t={t} />
          <SummaryChip label="NV" value={summary.nv_count ?? 0} color={FAIL} t={t} />
        </div>
      </div>

      {/* Pour header / limits editor */}
      <details className="mb-3 rounded" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
        <summary className="px-3 py-2 cursor-pointer font-semibold text-[11px]" style={{ color: t.text }}>
          Pour header & V/NV limits <span className="text-[10px]" style={{ color: t.textDim }}>(click to edit)</span>
        </summary>
        <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-2 border-t" style={{ borderColor: t.cardBorder }}>
          <TxtField t={t} label="Element" value={pourState.element || ''} onChange={v => patchPour({ element: v })} />
          <TxtField t={t} label="BIM / Section ID" value={pourState.bim_id || ''} onChange={v => patchPour({ bim_id: v })} testid="header-bim-id" />
          <TxtField t={t} label="Pour date" type="date" value={pourState.pour_date || ''} onChange={v => patchPour({ pour_date: v })} />
          <TxtField t={t} label="Site responsible" value={pourState.site_responsible || ''} onChange={v => patchPour({ site_responsible: v })} />
          <TxtField t={t} label="Producer" value={pourState.producer || ''} onChange={v => patchPour({ producer: v })} />
          <TxtField t={t} label="Slump min (mm)" type="number" value={pourState.slump_min_mm ?? ''} onChange={v => patchPour({ slump_min_mm: v === '' ? null : Number(v) })} />
          <TxtField t={t} label="Slump max (mm)" type="number" value={pourState.slump_max_mm ?? ''} onChange={v => patchPour({ slump_max_mm: v === '' ? null : Number(v) })} />
          <TxtField t={t} label="Temp min (°C)" type="number" value={pourState.temp_min_c ?? ''} onChange={v => patchPour({ temp_min_c: v === '' ? null : Number(v) })} />
          <TxtField t={t} label="Temp max (°C)" type="number" value={pourState.temp_max_c ?? ''} onChange={v => patchPour({ temp_max_c: v === '' ? null : Number(v) })} />
          <TxtField t={t} label="Air min (%)" type="number" value={pourState.air_min_pct ?? ''} onChange={v => patchPour({ air_min_pct: v === '' ? null : Number(v) })} />
          <TxtField t={t} label="Air max (%)" type="number" value={pourState.air_max_pct ?? ''} onChange={v => patchPour({ air_max_pct: v === '' ? null : Number(v) })} />
          <TxtField t={t} label="Sample every (m³)" type="number" value={pourState.sample_every_m3 ?? ''} onChange={v => patchPour({ sample_every_m3: v === '' ? null : Number(v) })} />
          <div>
            <div className="text-[10px] font-semibold mb-0.5" style={{ color: t.textDim }}>Status</div>
            <select value={pourState.status || 'open'}
              onChange={e => patchPour({ status: e.target.value })}
              className="w-full rounded px-2 py-1"
              style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text, fontSize: 11 }}>
              <option value="open">open</option>
              <option value="closed">closed</option>
            </select>
          </div>
        </div>
      </details>

      {/* ── Consolidated "Delivery & Placement" block ── */}
      <div className="mb-3 rounded" data-testid="itp-delivery-block"
        style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
        <div className="px-3 py-2 border-b flex items-center justify-between flex-wrap gap-2" style={{ borderColor: t.cardBorder }}>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: t.accent }}>
              {deliveryPhase?.label || 'Delivery & Placement — Pouring Record'}
            </div>
            <div className="text-[10px]" style={{ color: t.textMuted }}>
              {itp?.standard || 'FHWA FP-24 §552.09 · Table 552-10'} · live V/NV flagging as you type
            </div>
          </div>
          <button onClick={addRow} disabled={saving}
            className="px-2 py-1 rounded text-[11px] font-semibold flex items-center gap-1"
            style={{ background: t.accent, color: t.bg, opacity: saving ? 0.6 : 1 }}
            data-testid="itp-add-row">
            <Plus className="w-3 h-3" /> + Truckload
          </button>
        </div>

        {/* D-1..D-5 test chips */}
        {deliveryPhase && (
          <div className="px-3 pt-2 pb-1 flex flex-wrap gap-1.5 text-[10px]">
            {deliveryPhase.rows.map(r => (
              <div key={r.code} className="px-1.5 py-1 rounded"
                style={{ background: t.inputBg, border: `1px solid ${t.cardBorder}`, color: t.textMuted }}
                title={`${r.test_method} · ${r.frequency}`}>
                <span className="font-mono font-bold" style={{ color: t.accent }}>{r.code}</span>
                <span className="mx-1" style={{ color: t.text }}>{r.title.split('—')[0].trim()}</span>
                <span style={{ color: t.textDim }}>· {r.acceptance}</span>
              </div>
            ))}
          </div>
        )}

        {/* Samples cross-ref */}
        <div className="px-3 py-2 flex items-center gap-2 flex-wrap"
          style={{ background: (summary.samples ?? 0) > 0 ? 'rgba(34,197,94,0.06)' : 'transparent', borderTop: `1px dashed ${t.cardBorder}` }}
          data-testid="itp-samples-xref">
          <CheckCircle2 className="w-3.5 h-3.5"
            style={{ color: (summary.samples ?? 0) > 0 ? PASS : t.textDim }} />
          <div className="text-[11px]" style={{ color: t.text }}>
            <strong style={{ color: (summary.samples ?? 0) > 0 ? PASS : t.textMuted }}>
              {summary.samples ?? 0} sample{(summary.samples ?? 0) === 1 ? '' : 's'} taken
            </strong>
            <span className="ml-1" style={{ color: t.textMuted }}>
              (fill a Sample ID on any truck row to flag one)
            </span>
          </div>
          <div className="flex items-center gap-1 ml-auto px-2 py-0.5 rounded text-[10px]"
            style={{ background: t.inputBg, color: t.textMuted }}>
            <ExternalLink className="w-3 h-3" />
            Strength results live in <strong style={{ color: t.text }}>QC · Concrete → Samples &amp; Results</strong>
          </div>
        </div>

        {/* Truckload records grid */}
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr style={{ background: t.inputBg, color: t.textMuted }}>
                {['#', 'Arrived', 'Tested', 'Truck plate', 'Ticket #', 'Load m³', 'Cum. m³', 'Sample ID', 'Slump (mm)', 'Temp (°C)', 'Air (%)', 'Notes', 'Evidence', 'V/NV', ''].map((h, i) => (
                  <th key={i} className="px-1.5 py-1 text-left whitespace-nowrap"
                    style={{ borderBottom: `1px solid ${t.cardBorder}`, borderTop: `1px solid ${t.cardBorder}`, fontSize: 10 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.length === 0 && (
                <tr><td colSpan={15} className="p-4 text-center" style={{ background: t.card, color: t.textDim }}>
                  No trucks logged yet — click <strong>+ Truckload</strong>.
                </td></tr>
              )}
              {records.map(rec => (
                <RecordRow key={rec.id} t={t} rec={rec} pour={pourState}
                  onChange={(patch) => patchRow(rec.id, patch)}
                  onDelete={() => deleteRow(rec.id)} />
              ))}
            </tbody>
          </table>
        </div>

        <p className="px-3 py-2 text-[10px]" style={{ color: t.textDim, borderTop: `1px dashed ${t.cardBorder}` }}>
          Cumulative m³ is auto-computed from the load column. Green/red tinting on slump, temp and air cells reflects live compliance against this pour's V/NV limits.
        </p>
      </div>

      {/* Rest of the ITP reference (source / mix design / production / finished) */}
      {otherPhases.length > 0 && (
        <details className="mb-3 rounded" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <summary className="px-3 py-2 cursor-pointer font-semibold text-[11px]" style={{ color: t.text }}>
            ITP reference · {itp.label_short}
            <span className="text-[10px] ml-1" style={{ color: t.textDim }}>
              (other phases — source, mix design, production, finished product)
            </span>
          </summary>
          <div className="p-3 border-t" style={{ borderColor: t.cardBorder }}>
            {otherPhases.map(ph => (
              <div key={ph.key} className="mb-3">
                <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: t.accent }}>{ph.label}</div>
                <div className="space-y-1">
                  {ph.rows.map(r => (
                    <div key={r.code} className="p-1.5 rounded text-[10px]" style={{ background: t.inputBg }}>
                      <div className="flex gap-2">
                        <span className="font-mono" style={{ color: t.accent }}>{r.code}</span>
                        <span style={{ color: t.text }}>{r.title}</span>
                      </div>
                      <div style={{ color: t.textMuted }}>
                        <strong>Method:</strong> {r.test_method} · <strong>Frequency:</strong> {r.frequency} · <strong>Accept:</strong> {r.acceptance}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

const SummaryChip = ({ t, label, value, color, testid }) => (
  <div className="px-2 py-1 rounded text-[10px] text-center" style={{ background: `${color}1a`, border: `1px solid ${color}55`, minWidth: 60 }} data-testid={testid}>
    <div className="font-bold" style={{ color }}>{value}</div>
    <div style={{ color: t.textMuted }}>{label}</div>
  </div>
);

/* =======================================================================
   RecordRow — one truckload. Local state drives INSTANT V/NV flagging on
   slump / temp / air inputs (independent of the server-side `rec.verdict`).
   Server commit happens on blur (or Enter).
   ======================================================================= */
function RecordRow({ t, rec, pour, onChange, onDelete }) {
  const [local, setLocal] = useState(rec);
  useEffect(() => { setLocal(rec); }, [rec]);

  const commit = (field, raw) => {
    const isNum = ['load_m3', 'slump_mm', 'temp_c', 'air_pct', 'unit_weight_kgm3', 'water_added_l'].includes(field);
    const v = raw === '' ? null : (isNum ? Number(raw) : raw);
    if (v === rec[field]) return;
    onChange({ [field]: v });
  };

  // LIVE verdict — recomputed every render from local (un-committed) state
  const liveV = {
    slump: verdictFor('slump_mm', local.slump_mm, pour),
    temp:  verdictFor('temp_c', local.temp_c, pour),
    air:   verdictFor('air_pct', local.air_pct, pour),
  };
  liveV.overall = overallVerdict(liveV);

  const onPhotoPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const compressed = await Promise.all(files.map(f => compressImage(f)));
    onChange({ photos: [...(rec.photos || []), ...compressed] });
    e.target.value = '';
  };

  const onDocketPick = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    const dataUrl = await new Promise((resolve) => {
      const r = new FileReader(); r.onload = () => resolve(r.result); r.readAsDataURL(file);
    });
    onChange({ docket_pdf: { name: file.name, data: dataUrl, size: file.size } });
    e.target.value = '';
  };

  const Cell = ({ field, type = 'text', width = 70, testid }) => (
    <td className="px-1 py-0.5" style={{ borderBottom: `1px solid ${t.cardBorder}`, background: t.card }}>
      <input type={type} value={local[field] ?? ''}
        onChange={e => setLocal(prev => ({ ...prev, [field]: type === 'number' ? e.target.value : e.target.value }))}
        onBlur={e => commit(field, e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        className="w-full rounded px-1 py-0.5"
        style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text, fontSize: 11, minWidth: width }}
        data-testid={testid} />
    </td>
  );

  // Variant of Cell that tints itself based on live verdict
  const VCell = ({ field, verdictKey, width = 70, testid }) => {
    const v = liveV[verdictKey];
    const bg = verdictColorBg(v);
    const border = verdictBorderColor(v);
    return (
      <td className="px-1 py-0.5"
        style={{ borderBottom: `1px solid ${t.cardBorder}`, background: bg || t.card, transition: 'background-color 120ms' }}>
        <input type="number" value={local[field] ?? ''}
          onChange={e => setLocal(prev => ({ ...prev, [field]: e.target.value }))}
          onBlur={e => commit(field, e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="w-full rounded px-1 py-0.5 font-semibold"
          style={{
            background: t.inputBg,
            border: `1px solid ${border || t.inputBorder}`,
            color: v === 'NV' ? FAIL : (v === 'V' ? PASS : t.text),
            fontSize: 11,
            minWidth: width,
          }}
          data-testid={testid} />
      </td>
    );
  };

  const sampleFilled = !!(local.sample_id || '').toString().trim();

  return (
    <tr data-testid={`itp-rec-${rec.id}`}>
      <td className="px-1.5 py-0.5 font-mono text-[10px]" style={{ borderBottom: `1px solid ${t.cardBorder}`, background: t.card, color: t.textDim }}>{rec.seq}</td>
      <Cell field="time_arrived" type="time" width={80} />
      <Cell field="time_tested" type="time" width={80} />
      <Cell field="truck_plate" width={90} />
      <Cell field="ticket_no" width={90} />
      <Cell field="load_m3" type="number" width={60} />
      <td className="px-1 py-0.5 text-right" style={{ borderBottom: `1px solid ${t.cardBorder}`, background: t.card, color: t.textDim }}>
        {rec.cumulative_m3 ?? '—'}
      </td>
      {/* Sample ID — shows green chip when filled */}
      <td className="px-1 py-0.5" style={{ borderBottom: `1px solid ${t.cardBorder}`, background: sampleFilled ? 'rgba(34,197,94,0.08)' : t.card }}>
        <div className="flex items-center gap-1">
          <input type="text" value={local.sample_id ?? ''}
            onChange={e => setLocal(prev => ({ ...prev, sample_id: e.target.value }))}
            onBlur={e => commit('sample_id', e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            className="w-full rounded px-1 py-0.5"
            style={{ background: t.inputBg, border: `1px solid ${sampleFilled ? PASS : t.inputBorder}`, color: t.text, fontSize: 11, minWidth: 90 }}
            data-testid={`itp-sample-id-${rec.id}`} />
          {sampleFilled && (
            <CheckCircle2 className="w-3 h-3 flex-shrink-0" style={{ color: PASS }}
              title="Sample taken — strength results in Concrete → Samples & Results" />
          )}
        </div>
      </td>
      {/* LIVE-tinted compliance cells */}
      <VCell field="slump_mm" verdictKey="slump" width={70} testid={`itp-slump-${rec.id}`} />
      <VCell field="temp_c" verdictKey="temp" width={60} testid={`itp-temp-${rec.id}`} />
      <VCell field="air_pct" verdictKey="air" width={60} testid={`itp-air-${rec.id}`} />
      <Cell field="notes" width={160} />
      <td className="px-1 py-0.5" style={{ borderBottom: `1px solid ${t.cardBorder}`, background: t.card }}>
        <label className="cursor-pointer inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]"
          style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.textMuted }}
          title="Attach photos from camera (auto-compressed)">
          <Camera className="w-3 h-3" />
          {(rec.photos || []).length > 0 ? `${rec.photos.length} ph.` : 'Photos'}
          <input type="file" accept="image/*" capture="environment" multiple onChange={onPhotoPick} className="hidden" />
        </label>
        <label className="ml-1 cursor-pointer inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]"
          style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.textMuted }}
          title="Attach batch-ticket PDF">
          <FileText className="w-3 h-3" />
          {rec.docket_pdf ? 'PDF' : 'Docket'}
          <input type="file" accept="application/pdf" onChange={onDocketPick} className="hidden" />
        </label>
      </td>
      <td className="px-1.5 py-0.5" style={{ borderBottom: `1px solid ${t.cardBorder}`, background: t.card }} data-testid={`itp-verdict-${rec.id}`}>
        <div className="flex gap-0.5 text-[9px] flex-wrap">
          <Badge label="S" v={liveV.slump} />
          <Badge label="T" v={liveV.temp} />
          <Badge label="A" v={liveV.air} />
          <span className="px-1 py-0 rounded font-bold"
            style={{ background: verdictColorBg(liveV.overall) || 'transparent', color: liveV.overall === 'V' ? PASS : liveV.overall === 'NV' ? FAIL : MUTED }}
            data-testid={`itp-verdict-overall-${rec.id}`}>
            {liveV.overall}
          </span>
        </div>
      </td>
      <td className="px-1 py-0.5" style={{ borderBottom: `1px solid ${t.cardBorder}`, background: t.card }}>
        <button onClick={onDelete} className="p-0.5 rounded hover:bg-white/5" title="Delete row">
          <Trash2 className="w-3 h-3" style={{ color: t.textDim }} />
        </button>
      </td>
    </tr>
  );
}

const Badge = ({ label, v }) => {
  const col = v === 'V' ? PASS : v === 'NV' ? FAIL : MUTED;
  const Icon = v === 'V' ? CheckCircle2 : v === 'NV' ? AlertCircle : Beaker;
  return (
    <span className="px-1 py-0 rounded inline-flex items-center gap-0.5"
      style={{ background: `${col}1a`, color: col }} title={`${label}: ${v || 'NA'}`}>
      <Icon className="w-2.5 h-2.5" /> {label}
    </span>
  );
};

/* =======================================================================
   PriorisQCItpSidebar — compact list used by the Prioris shell sidebar.
   Exported so PriorisApp can render it under the "+ ITP" entry.
   ======================================================================= */
export function PriorisQCItpSidebar({ t, projectId, pours, selectedPourId, onSelectPour, onNewPour, onAllPours }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return pours.slice(0, 12);
    return pours.filter(p => {
      const hay = `${p.pour_ref || ''} ${p.element || ''} ${p.bim_id || ''} ${p.spec_rubrica_code || ''} ${p.pour_date || ''}`.toLowerCase();
      return hay.includes(s);
    }).slice(0, 20);
  }, [pours, q]);

  if (!projectId) return null;

  return (
    <div className="ml-3 space-y-0.5" data-testid="itp-sidebar-list">
      <button onClick={onNewPour}
        className="w-full flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
        style={{ color: t.accent, background: t.accentBg }}
        data-testid="itp-sidebar-new">
        <Plus className="w-2.5 h-2.5" /> New pour
      </button>
      <button onClick={onAllPours}
        className="w-full text-left px-2 py-0.5 rounded text-[11px]"
        style={{ color: selectedPourId == null ? t.accent : t.textDim, background: selectedPourId == null ? t.accentBg : 'transparent' }}
        data-testid="itp-sidebar-all">
        All pours ({pours.length})
      </button>
      <div className="px-1 py-0.5 flex items-center gap-1"
        style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, borderRadius: 4 }}>
        <Search className="w-2.5 h-2.5" style={{ color: t.textDim }} />
        <input type="text" value={q} onChange={e => setQ(e.target.value)}
          placeholder="Filter pours…"
          className="w-full bg-transparent outline-none"
          style={{ color: t.text, fontSize: 10 }}
          data-testid="itp-sidebar-filter" />
      </div>
      {filtered.length === 0 && (
        <div className="px-2 py-1 text-[10px] italic" style={{ color: t.textDim }}>No pours match.</div>
      )}
      {filtered.map(p => (
        <button key={p.id} onClick={() => onSelectPour(p.id)}
          className="w-full text-left px-2 py-0.5 rounded"
          style={{
            background: selectedPourId === p.id ? t.accentBg : 'transparent',
            color: selectedPourId === p.id ? t.accent : t.textDim,
          }}
          data-testid={`itp-sidebar-pour-${p.id}`}
          title={`${p.pour_ref || p.id.slice(0,6)} · ${p.element || ''} · ${p.pour_date || ''}`}>
          <div className="truncate text-[10px] font-semibold" style={{ color: selectedPourId === p.id ? t.accent : t.text }}>
            {p.pour_ref || p.id.slice(0, 6).toUpperCase()}
          </div>
          <div className="truncate text-[9px]" style={{ color: t.textMuted }}>
            {p.element || '—'}{p.bim_id ? ` · ${p.bim_id}` : ''} · {p.pour_date || ''}
          </div>
        </button>
      ))}
    </div>
  );
}
